import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  HStack,
  IconButton,
  Image,
  Input,
  Link,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  Icon,
  useToast,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaInstagram, FaLinkedinIn, FaTwitter, FaApple, FaGooglePlay } from 'react-icons/fa';
import { SiVisa, SiMastercard, SiPaypal, SiStripe, SiBitcoin } from 'react-icons/si';
import apiService from '../../services/apiService';

const COL_INVEST = [
  { label: 'Browse Properties', to: '/investments' },
  { label: 'How It Works', to: '/how-it-works' },
  { label: 'Investing Guide', to: '/guides' },
  { label: 'Fees & Details', to: '/faq' },
];

const COL_COMPANY = [
  { label: 'About Us', to: '/about' },
  { label: 'Our Team', to: '/team' },
  { label: 'Careers', to: '/careers' },
  { label: 'News & Updates', to: '/updates' },
];

const COL_RESOURCES = [
  { label: 'Investment Guides', to: '/guides' },
  { label: 'Investment Calculator', to: '/calculator' },
  { label: 'Market Insights', to: '/market-insights' },
  { label: 'Help Center', to: '/faq' },
];

const Column = ({ title, links }) => (
  <Stack spacing={2} align="flex-start">
    <Text color="#F5F7FA" fontSize="sm" fontWeight={600}>
      {title}
    </Text>
    {links.map((link) => (
      <Link
        key={link.label}
        as={RouterLink}
        to={link.to}
        color="#9AA7B8"
        fontSize="xs"
        _hover={{ color: '#D4A85F', textDecoration: 'none' }}
      >
        {link.label}
      </Link>
    ))}
  </Stack>
);

const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Please enter a valid email.', status: 'warning', duration: 2500, isClosable: true });
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiService.apiCall('/api/contact/newsletter', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      if (!response.ok) throw new Error('Newsletter request failed');

      toast({ title: 'Subscribed.', status: 'success', duration: 2500, isClosable: true });
      setEmail('');
    } catch (error) {
      toast({ title: 'Could not subscribe right now.', status: 'error', duration: 2500, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box as="form" onSubmit={onSubmit}>
      <Text color="#9AA7B8" fontSize="xs" lineHeight={1.6} mb={4}>
        Subscribe to our newsletter for investment opportunities and market insights.
      </Text>
      <Stack direction={{ base: 'column', sm: 'row' }} spacing={2} align="stretch">
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          h="34px"
          bg="#071326"
          borderColor="rgba(255,255,255,0.16)"
          borderRadius="6px"
          color="#F5F7FA"
          fontSize="xs"
          _placeholder={{ color: '#6B7B8F' }}
          _hover={{ borderColor: 'rgba(212,168,95,0.35)' }}
          _focus={{ borderColor: '#D4A85F', boxShadow: 'none' }}
        />
        <Button
          type="submit"
          variant="primary"
          h="34px"
          px={5}
          fontSize="xs"
          isLoading={submitting}
        >
          Subscribe
        </Button>
      </Stack>
    </Box>
  );
};

const PaymentTile = ({ icon, label, iconSize = 6 }) => (
  <Tooltip label={label} placement="top" bg="#0A1B34" color="#F5F7FA" fontSize="xs" hasArrow>
    <Box
      h="34px"
      minW="58px"
      px={3}
      borderRadius="6px"
      bg="rgba(255,255,255,0.04)"
      border="1px solid"
      borderColor="rgba(255,255,255,0.10)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      transition="all 0.25s ease"
      _hover={{ borderColor: 'rgba(212,168,95,0.35)', bg: 'rgba(255,255,255,0.06)' }}
    >
      <Icon as={icon} boxSize={iconSize} color="#D6DCE5" />
    </Box>
  </Tooltip>
);

const ComingSoonPill = ({ icon, label }) => (
  <HStack
    spacing={2}
    px={3}
    py={1.5}
    borderRadius="6px"
    bg="rgba(212,168,95,0.08)"
    border="1px solid"
    borderColor="rgba(212,168,95,0.22)"
    display="inline-flex"
    w="fit-content"
  >
    <Icon as={icon} boxSize={3.5} color="#D4A85F" />
    <Text fontSize="xs" color="#D6DCE5" fontWeight={500}>
      {label}
    </Text>
    <Text
      fontSize="9px"
      color="#D4A85F"
      fontWeight={700}
      letterSpacing="0.10em"
      textTransform="uppercase"
      bg="rgba(212,168,95,0.16)"
      px={1.5}
      py={0.5}
      borderRadius="3px"
      ml={1}
    >
      Soon
    </Text>
  </HStack>
);

const AppStoreTile = ({ icon, eyebrow, label }) => (
  <HStack
    spacing={2.5}
    px={3.5}
    py={2}
    borderRadius="8px"
    bg="rgba(255,255,255,0.04)"
    border="1px solid"
    borderColor="rgba(255,255,255,0.10)"
    transition="all 0.25s ease"
    cursor="not-allowed"
    _hover={{ borderColor: 'rgba(212,168,95,0.30)', bg: 'rgba(255,255,255,0.06)' }}
  >
    <Icon as={icon} boxSize={5} color="#D6DCE5" />
    <Box>
      <Text
        fontSize="9px"
        color="#9AA7B8"
        lineHeight={1}
        letterSpacing="0.08em"
        textTransform="uppercase"
        fontWeight={600}
      >
        {eyebrow}
      </Text>
      <Text fontSize="sm" color="#F5F7FA" fontWeight={600} lineHeight={1.1} mt={0.5}>
        {label}
      </Text>
    </Box>
  </HStack>
);

const Footer = () => {
  return (
    <Box as="footer" bg="#071326" color="#F5F7FA" py={{ base: 5, md: 5 }}>
      <Container maxW="container.xl" px={{ base: 5, md: 8 }}>
        <Box
          bg="#0A1B34"
          border="1px solid"
          borderColor="rgba(255,255,255,0.10)"
          borderRadius="8px"
          px={{ base: 5, md: 7 }}
          py={{ base: 6, md: 6 }}
        >
          <SimpleGrid columns={{ base: 2, lg: 5 }} spacing={{ base: 6, md: 8, lg: 10 }}>
            <Box gridColumn={{ base: 'span 2', lg: 'span 1' }}>
              <Box mb={4}>
                <Image
                  src="/images/BLP.png"
                  alt="Blue Lagoon & Partners"
                  h="44px"
                  w="auto"
                  objectFit="contain"
                />
              </Box>
              <Text color="#9AA7B8" fontSize="xs" lineHeight={1.6} maxW="230px" mb={4}>
                Empowering investors to build wealth through premium real estate opportunities.
              </Text>
              <HStack spacing={2}>
                {[
                  { label: 'LinkedIn', icon: <FaLinkedinIn />, href: null },
                  { label: 'Twitter', icon: <FaTwitter />, href: 'https://x.com/BluLagoonPrtnrs' },
                  { label: 'Instagram', icon: <FaInstagram />, href: null },
                ].map((s) => (
                  <IconButton
                    key={s.label}
                    aria-label={s.label}
                    icon={s.icon}
                    variant="ghost"
                    size="sm"
                    boxSize="40px"
                    minW="40px"
                    color="#D6DCE5"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.10)"
                    borderRadius="8px"
                    transition="all 0.2s ease"
                    {...(s.href
                      ? { as: 'a', href: s.href, target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                    _hover={{
                      color: '#D4A85F',
                      bg: 'rgba(212,168,95,0.08)',
                      borderColor: 'rgba(212,168,95,0.32)',
                    }}
                  />
                ))}
              </HStack>
            </Box>

            <Column title="Invest" links={COL_INVEST} />
            <Column title="Company" links={COL_COMPANY} />
            <Box gridColumn={{ base: 'span 2', lg: 'span 1' }}>
              <Column title="Resources" links={COL_RESOURCES} />
            </Box>

            <Box gridColumn={{ base: 'span 2', lg: 'span 1' }}>
              <Text color="#F5F7FA" fontSize="sm" fontWeight={600} mb={2}>
                Stay Updated
              </Text>
              <NewsletterForm />
            </Box>
          </SimpleGrid>

          <Divider borderColor="rgba(255,255,255,0.10)" my={{ base: 6, md: 6 }} />

          <Flex
            direction={{ base: 'column', lg: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', lg: 'flex-start' }}
            gap={8}
          >
            <Box flex="1">
              <Text fontSize="xs" color="#9AA7B8" lineHeight={1.6} mb={2}>
                <Text as="span" color="#D4A85F" fontWeight={600}>*Blended Portfolio Yield:</Text>{' '}
                This figure represents the weighted average return across our diversified portfolio,
                combining high-yield commercial units, short-term vacation rentals, and stable long-term
                residential leases.
              </Text>
              <Text fontSize="xs" color="#9AA7B8" lineHeight={1.6} mb={2}>
                The information provided on this website is for general informational purposes only and
                does not constitute legal, financial, or investment advice. Bluelagoon and Partners LLC
                makes no representations or warranties regarding the accuracy, completeness, or reliability
                of any information on this site. Property investments carry inherent risks and past
                performance does not guarantee future results. All business activities are subject to the
                applicable laws and regulations of St. Kitts and Nevis. By using this website, you agree
                to our Terms of Use and Privacy Policy.
              </Text>
              <Text fontSize="xs" color="#9AA7B8" mb={3}>
                Registered in St. Kitts and Nevis (Company Registration # 2415211). © 2026 bluelagoon and
                partners LLC. All rights reserved.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} fontSize="xs" color="#9AA7B8">
                <Box>
                  <Text fontWeight={600} color="#D6DCE5" mb={1}>Registered Office:</Text>
                  <Text>Building 112, Suite 9, Sebastian Street, Basseterre, St. Kitts &amp; Nevis</Text>
                </Box>
                <Box>
                  <Text fontWeight={600} color="#D6DCE5" mb={1}>Dubai Office:</Text>
                  <Text>3623, 36th Floor API Trio Tower, Sheikh Zayed Road, Al Barsha 1, Dubai, United Arab Emirates</Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Box minW={{ lg: '280px' }}>
              <Text fontSize="sm" fontWeight={600} color="#D6DCE5" mb={3}>
                Accepted Payment Methods
              </Text>
              <Flex align="center" gap={2} wrap="wrap" mb={3}>
                <PaymentTile icon={SiVisa} label="Visa" iconSize={7} />
                <PaymentTile icon={SiMastercard} label="Mastercard" />
                <PaymentTile icon={SiPaypal} label="PayPal" />
                <PaymentTile icon={SiStripe} label="Stripe" />
              </Flex>
              <ComingSoonPill icon={SiBitcoin} label="Crypto Payments" />
              <Text fontSize="xs" color="#9AA7B8" mt={3} mb={5}>
                SSL encrypted &amp; secure
              </Text>

              <Text fontSize="sm" fontWeight={600} color="#D6DCE5" mb={3}>
                Mobile Apps
              </Text>
              <Flex align="center" gap={2} wrap="wrap">
                <AppStoreTile icon={FaApple} eyebrow="Coming soon to" label="App Store" />
                <AppStoreTile icon={FaGooglePlay} eyebrow="Coming soon to" label="Google Play" />
              </Flex>
            </Box>
          </Flex>
        </Box>

        <Flex
          mt={4}
          px={{ base: 1, md: 3 }}
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          gap={3}
        >
          <Text color="#6B7B8F" fontSize="xs">
            © 2026 BlueLagoon &amp; Partners. All rights reserved.
          </Text>
          <HStack spacing={{ base: 4, md: 8 }} fontSize="xs" color="#6B7B8F" flexWrap="wrap">
            <Link as={RouterLink} to="/privacy" _hover={{ color: '#D4A85F', textDecoration: 'none' }}>
              Privacy Policy
            </Link>
            <Link as={RouterLink} to="/terms" _hover={{ color: '#D4A85F', textDecoration: 'none' }}>
              Terms of Service
            </Link>
            <Link as={RouterLink} to="/disclaimer" _hover={{ color: '#D4A85F', textDecoration: 'none' }}>
              Risk Disclosure
            </Link>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
};

export default Footer;
